/**
 * Aba "Personalidade" da ficha: sete colunas de adjetivos para marcar, editável pela consultora.
 *
 * Porte de `portal/admin.html:2482-2615` (formulário), `4890-4920` (preencher e salvar) e
 * `5030-5035` (leitura). Linhas do inventário: 192 e 193.
 *
 * Território da frente A2. A moldura (`../index.tsx`) importa esta aba pelo nome `AbaPersonalidade`.
 */

// Estado e retorno estável do React, e o tipo do envio do formulário.
import { useCallback, useState, type FormEvent } from 'react';
// O botão do Chassi.
import { Botao } from '@complexity/ui';
// A leitura e a gravação da aba, no escopo da ficha.
import { lerPersonalidade, salvarPersonalidade } from '@studiolu/dados/areas/ficha';
// A fonte de dados.
import { usePainel } from '../../../contexto.js';
// O status que some sozinho (A5).
import { LinhaDeStatus, useStatusTemporario } from '../../../comum/status-temporario.js';
// Os rótulos das colunas, da cópia única.
import { PERGUNTAS_PERSONALIDADE } from '../constantes.js';
// As caixas de marcar e a leitura do legado.
import { GrupoDeMarcacao, alternarMarcado, opcoesDe } from '../comum-ficha/Marcacoes.js';
import { contem } from '../comum-ficha/regras.js';
import type { FichaAberta, PropsDaAba } from '../comum-ficha/tipos.js';
// O contrato de carga: a moldura lê esta aba antes de mostrá-la e relê a cada toque no botão dela.
import { useCarregadorDaAba } from '../comum-ficha/carregador.js';
// Os apoios comuns das abas (A3).
import { AbaSemFicha } from './comum-a3/sem-ficha.js';
import { useEscopoEstavel } from './comum-a3/escopo.js';
import { useRecortePorPapel } from './comum-a3/papel.js';

/**
 * Os 77 adjetivos, 11 por coluna, na ordem do HTML do legado (`admin.html:2490-2610`). A ordem
 * importa: é a ordem na tela e a ordem em que a lista é gravada.
 */
export const ADJETIVOS_PERSONALIDADE: Readonly<Record<string, readonly string[]>> = Object.freeze({
  coluna_1: ['Ativa', 'Acessível', 'Casual', 'Animada', 'Competitiva', 'Energética', 'Entusiasmada', 'Amigável', 'Natural', 'Otimista', 'Espontânea'],
  coluna_2: ['Apropriada', 'Competente', 'Conservadora', 'Convencional', 'Leal', 'Organizada', 'Prática', 'Tradicional', 'Confiável', 'Responsável', 'Comprometida'],
  coluna_3: ['Culta', 'Solene', 'Atenta', 'Formal', 'Meticulosa', 'Equilibrada', 'Correta', 'Refinada', 'Reservada', 'Serena', 'Imponente'],
  coluna_4: ['Atenciosa', 'Compreensiva', 'Afetuosa', 'Discreta', 'Compassiva', 'Gentil', 'Protetora', 'Receptiva', 'Sentimental', 'Delicada', 'Acolhedora'],
  coluna_5: ['Corajosa', 'Atraente', 'Agitada', 'Conquistadora', 'Ousada', 'Magnética', 'Provocante', 'Sedutora', 'Autoconfiante', 'Sensual', 'Sexy'],
  coluna_6: ['Artística', 'Eclética', 'Emocional', 'Espírito Livre', 'Imaginativa', 'Independente', 'Inovadora', 'Original', 'Subjetiva', 'Não convencional', 'Única'],
  coluna_7: ['Distante', 'Sofisticada', 'Assertiva', 'Arrojada', 'Exigente', 'Notável', 'Segura', 'Intensa', 'Forte', 'Cosmopolita', 'Poderosa'],
});

// As sete colunas, na ordem do legado.
const COLUNAS = Object.keys(PERGUNTAS_PERSONALIDADE);

/** As marcadas de cada coluna, lidas com o `includes` do legado (`admin.html:4890-4897`). */
function preencher(linha: Readonly<Record<string, unknown>> | null): Record<string, readonly string[]> {
  const marcadas: Record<string, readonly string[]> = {};
  for (const coluna of COLUNAS) {
    const valores = linha?.[coluna] as readonly string[] | string | null | undefined;
    marcadas[coluna] = (ADJETIVOS_PERSONALIDADE[coluna] ?? []).filter((a) => contem(valores, a));
  }
  return marcadas;
}

export function AbaPersonalidade({ ficha }: PropsDaAba) {
  // Sem a ficha aberta, a aba não consulta nem grava nada.
  if (!ficha) return <AbaSemFicha aba="Personalidade" />;
  return <FormularioPersonalidade ficha={ficha} />;
}

function FormularioPersonalidade({ ficha }: { readonly ficha: FichaAberta }) {
  const { dados } = usePainel();
  const escopo = useEscopoEstavel(ficha);
  const { somenteLeitura } = useRecortePorPapel();
  // Nasce sem marcas, como `preencherFormularioPersonalidade(null)` da abertura (`5952`).
  const [marcadas, setMarcadas] = useState<Record<string, readonly string[]>>(() => preencher(null));
  // O salvar trava até a leitura voltar e enquanto grava (`5962`, `5054` e `4904`).
  const [carregado, setCarregado] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [status, mostrar, fixar] = useStatusTemporario();

  // A leitura (`carregarQuestionarios`, `admin.html:5030-5035`).
  const carregar = useCallback(async () => {
    const r = await lerPersonalidade(dados, escopo);
    if (!r.ok) console.error('Erro ao carregar questionário de personalidade:', r.erro);
    setMarcadas(preencher(r.ok ? (r.dados as Readonly<Record<string, unknown>> | null) : null));
    setCarregado(true);
  }, [dados, escopo]);

  /*
   * A leitura pelo contrato da moldura (`comum-ficha/carregador.ts`): ela roda ANTES de a aba
   * aparecer e de novo a cada toque no botão, como `CARREGADOR_ABA_CLIENTE` (`admin.html:5086-5091`).
   * As marcações voltam ao que está no banco (`preencherFormularioPersonalidade`, `4890-4897`), e a
   * linha de status não é tocada, então ela sobrevive à troca de aba, como no legado.
   */
  useCarregadorDaAba(carregar);

  // O salvar (`admin.html:4899-4920`): a lista de cada coluna e a contagem ao lado.
  const salvar = async (evento: FormEvent) => {
    evento.preventDefault();
    setSalvando(true);
    fixar('Salvando...');
    const registro: Record<string, unknown> = {};
    for (const coluna of COLUNAS) {
      const lista = [...(marcadas[coluna] ?? [])];
      registro[coluna] = lista;
      // `contagem_coluna_N`, gravada junto (`admin.html:4911`).
      registro[`contagem_${coluna}`] = lista.length;
    }
    const r = await salvarPersonalidade(dados, escopo, registro);
    setSalvando(false);
    if (!r.ok) {
      fixar(`Erro ao salvar: ${r.erro.mensagem}`);
      return;
    }
    mostrar('Salvo!');
    // O legado relê os questionários depois de gravar.
    await carregar();
  };

  return (
    <div className="flex flex-col gap-cx-4">
      <h3 className="text-cx-lg font-semibold text-texto">Personalidade</h3>
      <form onSubmit={(e) => void salvar(e)} className="flex flex-col gap-cx-6">
        <div className="grid grid-cols-1 gap-cx-6 sm:grid-cols-2 lg:grid-cols-3">
          {COLUNAS.map((coluna) => (
            <section key={coluna} className="flex flex-col gap-cx-2">
              <h4 className="border-b border-borda pb-cx-2 text-cx-base font-semibold text-texto">
                {PERGUNTAS_PERSONALIDADE[coluna]}
              </h4>
              {/* O título da coluna já é o cabeçalho; o grupo repete só para o leitor de tela. */}
              <GrupoDeMarcacao
                titulo={PERGUNTAS_PERSONALIDADE[coluna]!}
                opcoes={opcoesDe(ADJETIVOS_PERSONALIDADE[coluna] ?? [])}
                marcadas={(v) => (marcadas[coluna] ?? []).includes(v)}
                aoMudar={(v, marcada) =>
                  setMarcadas((m) => ({
                    ...m,
                    [coluna]: alternarMarcado(m[coluna] ?? [], ADJETIVOS_PERSONALIDADE[coluna] ?? [], v, marcada),
                  }))
                }
                desabilitado={somenteLeitura}
                ocultarTitulo
              />
            </section>
          ))}
        </div>
        {!somenteLeitura && (
          <Botao type="submit" className="self-start" disabled={!carregado || salvando}>
            Salvar / reenviar personalidade
          </Botao>
        )}
        <LinhaDeStatus texto={status} testid="status-personalidade" />
      </form>
    </div>
  );
}
